/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import { Grid, Typography, makeStyles } from '@material-ui/core';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import i18n from 'i18n';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { useParams } from 'react-router';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import RefreshButton from 'components/RefreshButton';
import TitleBehavior from 'containers/Environments/Configuration/FormBehavior/TitleBehavior';
import { getContextAuditLogsRequest } from 'modules/audit/actions';
import { AuditLogsResponse, AuditLogsResults } from 'modules/audit/interfaces';
import { cyrb53Hash } from 'utils/helpers';
import { formatDateValue } from 'utils/dateFormat';
import { ModalFields } from 'modules/modal/interfaces';
import { modalOpen, modalClose } from 'modules/modal/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import styles from '../styles';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
};

interface Props {
  isLoading: boolean;
  auditLogsData: AuditLogsResponse;
  getContextAuditLogsRequest: (props) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
}

const MigrateHistory = ({
  isLoading,
  auditLogsData,
  getContextAuditLogsRequest,
  modalOpen,
  modalClose,
}: Props) => {
  const classes = useStyles();

  const {
    itemOrder,
    query,
    searchValue,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);
  const [refresh, setRefresh] = React.useState(false);

  const { id } = useParams<{ id: string }>();

  const [headers] = React.useState([
    {
      key: 'created_at',
      i18n: 'MIGRATION_DATE_KEY',
      order: true,
    },
    {
      key: 'persistent_user_first_name',
      i18n: 'OPERATOR_KEY',
      order: true,
    },
    {
      key: '',
      i18n: 'ORIGIN_PARTNER_KEY',
    },
    {
      key: '',
      i18n: 'ORIGIN_COMPANY_KEY',
    },
    {
      key: '',
      i18n: 'DESTINATION_PARTNER_KEY',
    },
    {
      key: '',
      i18n: 'DESTINATION_COMPANY_KEY',
    },
    {
      key: '',
      i18n: 'SALT.REASON_SALT_KEY',
    },
    {
      key: '',
      i18n: 'LOGS_PAGE.DETAILS_KEY',
    },
  ]);

  const handleGetAuditLogs = () => {
    getContextAuditLogsRequest({
      query: { ...query },
      resource: 'environment',
      resource_uuid: id,
      action: 'migrate_resource',
    });
  };

  const getGetAuditLogs = (refresh = false) => {
    handleGetAuditLogs();
    setRefresh(refresh);
  };

  React.useEffect(() => {
    getGetAuditLogs();
  }, [query]);

  return (
    <Grid container>
      <TitleBehavior
        title={i18n.t('HISTORY_SOP_KEY')}
        subtitle={i18n.t('MIGRATION_HISTORY_SUBTITLE_KEY')}
      />
      <Grid container alignItems="center">
        <Grid item xs={11}>
          <SearchBar
            id="search-migration-history"
            isLoading={isLoading}
            searchValue={searchValue}
            handleSearch={handleSearch}
            setSearchValue={setSearchValue}
          />
        </Grid>
        <Grid item xs={1} justify="flex-end" className={classes.refresh}>
          <RefreshButton
            id="btn-refresh"
            onClick={() => handleGetAuditLogs()}
            animate={isLoading}
            disabled={isLoading}
          />
        </Grid>
      </Grid>
      <Grid container>
        <TableItems
          header={
            <>
              {headers.map(item => (
                <TableCell
                  key={item?.key}
                  order={item?.order}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                  markOderItem={itemOrder === item?.key}
                >
                  {i18n.t(item?.i18n)}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: AuditLogsResults, index) => (
            <TableRow key={cyrb53Hash(String(index))}>
              <TableCell className={classes.rowDate}>
                {formatDateValue(item?.created_at)}
              </TableCell>
              <TableCell className={classes.rowAllRowsCount}>
                {`${item?.persistent_user_first_name} ${item?.persistent_user_last_name}`}
              </TableCell>
              <TableCell className={classes.rowAllRowsCount}>
                {item?.details_json?.origin_partner_name}
              </TableCell>
              <TableCell className={classes.rowAllRowsCount}>
                {item?.details_json?.origin_company_name}
              </TableCell>
              <TableCell className={classes.rowAllRowsCount}>
                {item?.details_json?.destination_partner_name}
              </TableCell>
              <TableCell className={classes.rowAllRowsCount}>
                {item?.details_json?.destination_company_name}
              </TableCell>
              <TableCell className={classes.rowReason}>
                <Typography className={classes.rowReason}>
                  {item?.details_json?.migrate_reason}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <InfoOutlinedIcon
                  onClick={() =>
                    modalOpen({
                      title: i18n.t('MIGRATION_REASON_KEY'),
                      maxWidth: 'md',
                      content: (
                        <Typography className={classes.reasonText}>
                          {item?.details_json?.migrate_reason}
                        </Typography>
                      ),
                      genericFooter: {
                        action: () => {
                          modalClose();
                        },
                        label: i18n.t('CLOSE_KEY'),
                      },
                    })
                  }
                  className={classes.defaultIcon}
                />
              </TableCell>
            </TableRow>
          )}
          data={auditLogsData}
          isLoading={!refresh && isLoading}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('MONITOR.NO_RESULTS_INSTANCES')}
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected, data },
  audit: { auditLogsData, isLoading },
}: ApplicationState) => ({
  companySelected,
  role: data?.role,
  isLoading,
  auditLogsData,
});

export default connect(mapStateToProps, {
  getContextAuditLogsRequest,
  modalOpen,
  modalClose,
})(MigrateHistory);
